import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TextField, Button, Typography, Box, Paper, Alert, Stepper, Step, StepLabel, Tooltip, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import client from '../api/client';
import PhoneCountryPicker from '../components/PhoneCountryPicker';

const steps = ['Account Info', 'Business Details', 'WhatsApp Number'];

const Register = () => {
  const [form, setForm] = useState({
    fullName: '', email: '', password: '',
    mobile: '', address: '', city: '', state: '', country: '',
    companyName: '', whatsappBusinessNumber: '', whatsappCountryCode: '91', fbBusinessManagerId: ''
  });
  const [consent, setConsent] = useState({ terms: false, privacy: false });
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const field = (name, label, opts = {}) => (
    <TextField
      label={label}
      fullWidth required={opts.required !== false}
      type={opts.type || 'text'}
      value={form[name]}
      onChange={e => setForm({ ...form, [name]: e.target.value })}
      sx={{ mb: 2 }}
    />
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 4 || form.password.length > 10) {
      setError('Password must be 4-10 characters');
      return;
    }
    if (!form.whatsappBusinessNumber) {
      setError('WhatsApp Business Number is required');
      return;
    }
    try {
      await client.post('/api/whatsapp/register', {
        ...form,
        termsAccepted: consent.terms,
        privacyAccepted: consent.privacy,
      });
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    }
  };

  const canNext = () => {
    if (step === 0) return form.fullName && form.email && form.password;
    if (step === 1) return true;
    return form.whatsappBusinessNumber && form.fbBusinessManagerId && consent.terms && consent.privacy;
  };

  return (
    <Box className="water-bg" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8faff', py: 4 }}>
      <Paper className="glass-card" sx={{ p: 4, maxWidth: 540, width: '100%', borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box component="img" src="/talktokam-green-wa.png" alt="Talktokam"
            sx={{ height: 48, width: 'auto', borderRadius: '8px', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#075e54' }}>Create Account</Typography>
          <Typography variant="body2" color="text.secondary">Set up your WhatsApp API platform</Typography>
        </Box>

        <Stepper activeStep={step} sx={{ mb: 3, '& .MuiStepLabel-root .Mui-active': { color: '#075e54' }, '& .MuiStepLabel-root .Mui-completed': { color: '#075e54' } }}>
          {steps.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          {step === 0 && (
            <Box>
              {field('fullName', 'Full Name')}
              {field('email', 'Email', { type: 'email' })}
              {field('password', 'Password (4-10 chars)', { type: 'password' })}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Password must be 4-10 characters long
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={() => setStep(1)} disabled={!canNext()} variant="contained"
                  sx={{ px: 4 }}>
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {step === 1 && (
            <Box>
              {field('mobile', 'Personal Mobile Number', { required: false })}
              {field('companyName', 'Company Name', { required: false })}
              {field('address', 'Address', { required: false })}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>{field('city', 'City', { required: false })}</Box>
                <Box sx={{ flex: 1 }}>{field('state', 'State', { required: false })}</Box>
              </Box>
              {field('country', 'Country', { required: false })}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setStep(0)} variant="outlined"
                  sx={{ borderColor: '#075e54', color: '#075e54', px: 3 }}>
                  Back
                </Button>
                <Button onClick={() => setStep(2)} variant="contained"
                  sx={{ px: 4, flex: 1 }}>
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {step === 2 && (
            <Box>
              <Box sx={{ p: 2.5, bgcolor: 'rgba(7,94,84,0.06)', borderRadius: 2, border: '1px solid rgba(7,94,84,0.15)', mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#075e54', mb: 0.5 }}>
                  WhatsApp Business Number
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This number will be used for WhatsApp Business API. It should be a number NOT registered on the WhatsApp consumer app.
                </Typography>
              </Box>
              <PhoneCountryPicker
                countryCc={form.whatsappCountryCode}
                onChangeCc={(cc) => setForm({ ...form, whatsappCountryCode: cc })}
                value={form.whatsappBusinessNumber}
                onChange={(v) => setForm({ ...form, whatsappBusinessNumber: v })}
                numberLabel="WhatsApp Business Number"
                numberPlaceholder="e.g. 9876543210"
                helperText="Number without country code — select your country above"
              />

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#075e54' }}>
                    Facebook Business Manager ID
                  </Typography>
                  <Tooltip title={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>How to get your BM ID & grant access:</Typography>
                      <Typography variant="caption" component="div" sx={{ opacity: 0.9 }}>
                        1. Go to business.facebook.com{'\n'}
                        2. Settings → Business Info{'\n'}
                        3. Copy "Business Manager ID"{'\n'}{'\n'}
                        Grant access to talktokam-api:{'\n'}
                        4. Settings → Users → Add{'\n'}
                        5. Paste "talktokam-api"{'\n'}
                        6. Role: Admin → WhatsApp Business Management
                      </Typography>
                    </Box>
                  } arrow placement="right">
                    <IconButton size="small" sx={{ color: '#075e54', p: 0.3 }}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField fullWidth required
                  value={form.fbBusinessManagerId}
                  onChange={e => setForm({ ...form, fbBusinessManagerId: e.target.value.replace(/[^0-9]/g, "") })}
                  placeholder="2251190668973322"
                />
              </Box>

              <FormControlLabel
                control={<Checkbox checked={consent.terms} onChange={e => setConsent({ ...consent, terms: e.target.checked })} sx={{ color: '#075e54', '&.Mui-checked': { color: '#075e54' } }} />}
                label={<Typography variant="body2">I agree to the <Link to="/terms" target="_blank" style={{ color: '#075e54', fontWeight: 600 }}>Terms of Service</Link></Typography>}
                sx={{ mb: 0.5 }}
              />
              <FormControlLabel
                control={<Checkbox checked={consent.privacy} onChange={e => setConsent({ ...consent, privacy: e.target.checked })} sx={{ color: '#075e54', '&.Mui-checked': { color: '#075e54' } }} />}
                label={<Typography variant="body2">I agree to the <Link to="/privacy" target="_blank" style={{ color: '#075e54', fontWeight: 600 }}>Privacy Policy</Link></Typography>}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => setStep(1)} variant="outlined"
                  sx={{ borderColor: '#075e54', color: '#075e54', px: 3 }}>
                  Back
                </Button>
                <Button type="submit" variant="contained" disabled={!canNext()}
                  sx={{ px: 4, flex: 1 }}>
                  Create Account
                </Button>
              </Box>
            </Box>
          )}
        </form>

        <Typography variant="body2" sx={{ mt: 2.5, textAlign: 'center', color: '#64748b' }}>
          Already have an account? <Link to="/login" style={{ color: '#075e54', fontWeight: 600, textDecoration: 'none' }}>Login</Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Register;
